import {
  CSSObject,
  FormControlLabel,
  FormHelperText,
  InputBase,
  InputBaseProps,
  styled,
  Theme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { NumericFormatInput } from '@/components/atoms/NumericFormatInput';
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown';
import { CurrencyItem } from '@/components/molecules/CurrencyItem';
import { Currency } from '@/data/currencies/type';
import { useLocalizedToNumber } from '@/hooks/useLocalizedToNumber';
import { LocaleContext } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';

interface Props {
  readonly amount: number | null;
  readonly label: string;
  readonly currency: CurrencyCodeType;

  readonly currencies: readonly CurrencyCodeType[];
  readonly size?: 'small' | 'normal';

  readonly readOnly?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: boolean;
  readonly autoFocus?: boolean;
  readonly currencyDisabled?: boolean;

  readonly name?: string;
  readonly error?: boolean;
  readonly helperText?: string | null;

  onCurrencyChange?(currency: CurrencyCodeType): void;
  onAmountChange?(amount: number | null): void;
}

export const AmountAndCurrencyWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    amount,
    currency,
    label,
    currencies,
    size = 'normal',
    readOnly = false,
    disabled = false,
    placeholder = false,
    autoFocus = false,
    error = false,
    name,
    currencyDisabled = false,
    helperText,
  } = props;
  const { onCurrencyChange, onAmountChange } = props;

  const toNumber = useLocalizedToNumber();
  const locale = React.useContext<Locale>(LocaleContext);

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;

      if (value.length === 0) {
        onAmountChange?.(null);
      } else {
        const numeric = toNumber(value);
        if (!isNaN(numeric)) {
          onAmountChange?.(numeric);
        }
      }
    },
    [onAmountChange, toNumber],
  );

  const inputSx = React.useMemo((): SystemStyleObject => {
    switch (size) {
      case 'small':
        return styles.inputSmall;
      case 'normal':
        return styles.inputNormal;
    }
  }, [size]);

  const inputProps = React.useMemo(
    (): InputBaseProps['inputProps'] => ({
      disabled: disabled,
      readOnly: readOnly,
      sx: inputSx,
      onValueChange: onChange,
    }),
    [disabled, inputSx, onChange, readOnly],
  );

  const computedStyle = React.useMemo((): SystemStyleObject => {
    const height = size === 'small' ? 80 : 96;

    return {
      height,
      position: 'relative',
      borderRadius: 6,
      borderColor: error ? 'error.main' : undefined,
      pr: 2,
      pl: 4,
      py: '14px',
    };
  }, [error, size]);

  const onChangeCurrencyWrapper = React.useCallback(
    (currencyCode: string): void => {
      const currency = currencies.find((currency: string): boolean => currency === currencyCode);
      if (!currency) {
        console.debug(`currency not found: ${currencyCode}`);
        return;
      }

      onCurrencyChange?.(currency);
    },
    [currencies, onCurrencyChange],
  );

  const currencyElement = React.useMemo(
    (): React.ReactElement => (
      <Box sx={styles.adornment}>
        {currencies.length > 1 && !currencyDisabled ? (
          <CurrencyDropdown
            value={currency}
            label={label}
            currencies={currencies}
            onChange={onChangeCurrencyWrapper}
          />
        ) : (
          <CurrencyItem currency={Currency.fromCode(locale, currency)} />
        )}
      </Box>
    ),
    [currencies, currencyDisabled, currency, label, locale, onChangeCurrencyWrapper],
  );

  const startAdornment = React.useMemo((): React.ReactElement | undefined => {
    if (!placeholder) {
      return undefined;
    }

    return <Box sx={styles.placeholder} />;
  }, [placeholder]);

  const visibleValue = React.useMemo((): number | null => {
    if (placeholder) {
      return null;
    }

    return amount;
  }, [amount, placeholder]);

  return (
    <>
      <StyledFormControlLabel
        sx={computedStyle}
        labelPlacement="top"
        control={
          <InputBase
            name={name}
            inputProps={inputProps}
            value={visibleValue}
            fullWidth={true}
            startAdornment={startAdornment}
            endAdornment={currencyElement}
            disabled={disabled}
            readOnly={readOnly}
            inputComponent={NumericFormatInput}
            error={error}
            autoFocus={autoFocus}
            onChange={onChange}
          />
        }
        label={label}
      />
      {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
    </>
  );
};

const StyledFormControlLabel = styled(FormControlLabel)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'center',
    margin: 0,
    backgroundColor: theme.palette.custom.InputsBackground,
    borderColor: theme.palette.custom.DividerLight,
    borderStyle: 'solid',
    borderWidth: 1,
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '20px',
      color: theme.palette.custom.LightGray,
      margin: 0,
      padding: 0,
    },
    '& .MuiInputBase-input[readonly]': {
      pointerEvents: 'none',
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  inputSmall: {
    color: 'text.primary',
    padding: 0,
    fontSize: 24,
    fontWeight: 500,
    lineHeight: '32px',
    height: 32,
  },
  inputNormal: {
    color: 'text.primary',
    padding: 0,
    fontSize: 32,
    fontWeight: 500,
    lineHeight: '40px',
    height: 40,
  },
  adornment: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 32,
  },
  placeholder: {
    position: 'absolute',
    width: '50%',
    backgroundColor: 'custom.Skeleton',
    height: 16,
    bottom: 8,
    animation: 'skeleton 1s infinite',
    borderRadius: 2,
  },
};
